/**
 * Route gate: a page opens only for a user holding ONE of the modules listed for it in
 * modules/pageAccess.ts (#3989, Nick ruling R2 2026-10-01: guard each PAGE by module; no blanket
 * per-module-prefix gate). Creating or editing a bill is Billing (P4) only (Nick 2026-10-02), so
 * every Billing work page lists P4 alone; Projects and Field Service read bills through their own
 * read-only views. The Owner role always passes, as on the server, and an External account
 * (contractor) is refused unless the page sets allowExternal, also as on the server. The module
 * lists live in modules/pageAccess.ts; routes use modules/pageGuard.tsx; the nav
 * (NavItem.requirePage), landing options and links read the same lists through useCanOpenPage.
 *
 * This is a usability gate, NOT access control: every callable still checks its own module on the
 * server, and firestore.rules does its own gating (the QuickBooks money tables are Finance-only since
 * #6381 step C). It stops most users from landing on a page whose actions all fail.
 * A refused user sees an in-place "access required" card (no redirect), like FinanceAccessRoute.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { ModuleId } from '../modules/types';
import { useCanOpenPage } from '../modules/useCanOpenPage';
import { PageLayout } from './PageLayout';
import { Card } from './shared/Card';
import { EmptyState } from './shared/EmptyState';
import { Button } from './forms/Button';

export { useCanOpenPage };

/**
 * True when the user is an Owner or holds at least one of `anyOf`; an External account is
 * refused unless `allowExternal` (mirrors the server's External opt-in).
 */
export function useHasAnyModuleAccess(anyOf: readonly ModuleId[], allowExternal = false): boolean {
  return useCanOpenPage()(allowExternal ? { anyOf, allowExternal: true } : { anyOf });
}

export interface ModuleAccessRouteProps {
  anyOf: readonly ModuleId[];
  /** Admit External accounts (contractors). Off by default, as on the server. */
  allowExternal?: boolean;
  children: React.ReactNode;
  title?: string;
  description?: string;
  testId?: string;
}

export function ModuleAccessRoute({
  anyOf,
  allowExternal = false,
  children,
  title = 'Module access required',
  description = "This page belongs to a module you don't have. Ask an owner if you need access.",
  testId = 'module-no-access',
}: ModuleAccessRouteProps) {
  const allowed = useHasAnyModuleAccess(anyOf, allowExternal);
  const navigate = useNavigate();
  if (allowed) return <>{children}</>;
  return (
    <PageLayout>
      <Card data-testid={testId}>
        <EmptyState
          icon={Lock}
          title={title}
          description={description}
          action={
            <Button variant="secondary" onClick={() => navigate('/', { replace: true })}>
              Back to home
            </Button>
          }
        />
      </Card>
    </PageLayout>
  );
}
